import React, { useState } from 'react';
import PropTypes from 'prop-types';

import UTRModal from '../../../Modal';
import SavedListModalActions from '../../SavedListModalActions';
import { SORT_SHEET_TITLE, SORT_SHEET_HEADING, SORT_SHEET_APPLY_LABEL } from './constants';
import styles from './sortPlayerListSheet.scss';

// Mobile "Sort Player List" sheet (101-8030): the five sort options as radio rows (bold title over a
// grey subtitle) with a Cancel / Apply footer. Selection is staged locally and only committed on Apply,
// so Cancel leaves the current sort untouched; Apply is disabled until an option is chosen. The parent
// owns the feed sort — this component only stages and reports the chosen option. Rendered mobile-only,
// so it uses UTRModal's mobile full-sheet chrome (title + close), matching the sibling note sheet.
const SortPlayerListSheet = ({ options, selectedValue, onApply, onCancel, 'data-testid': dataTestId }) => {
    const [pendingValue, setPendingValue] = useState(selectedValue);

    const handleApply = () => {
        const option = options.find(item => item.value === pendingValue);
        if (option) {
            onApply(option);
        }
    };

    return (
        <UTRModal
            title={SORT_SHEET_TITLE}
            onCloseClick={onCancel}
            className={styles.sortSheet}
            data-testid={dataTestId}
        >
            <div className={styles.heading} data-testid={dataTestId && `${dataTestId}.heading`}>
                {SORT_SHEET_HEADING}
            </div>
            <div className={styles.options}>
                {options.map((option) => {
                    const optionTestId = dataTestId && `${dataTestId}.${option.testId}`;
                    const isChecked = pendingValue === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            className={styles.option}
                            onClick={() => setPendingValue(option.value)}
                            data-testid={optionTestId}
                        >
                            <span className={`${styles.radio} ${isChecked ? styles.radioChecked : ''}`} />
                            <span className={styles.optionText}>
                                <span className={styles.optionTitle} data-testid={optionTestId && `${optionTestId}.title`}>
                                    {option.title}
                                </span>
                                <span className={styles.optionSubtitle} data-testid={optionTestId && `${optionTestId}.subtitle`}>
                                    {option.subtitle}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
            <SavedListModalActions
                data-testid={dataTestId}
                onCancel={onCancel}
                onSave={handleApply}
                saveDisabled={!pendingValue}
                saveLabel={SORT_SHEET_APPLY_LABEL}
                cancelTestidSuffix="cancel"
                primaryTestidSuffix="apply"
            />
        </UTRModal>
    );
};

SortPlayerListSheet.propTypes = {
    options: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
    selectedValue: PropTypes.string,
    onApply: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

SortPlayerListSheet.defaultProps = {
    selectedValue: '',
    'data-testid': null,
};

export default SortPlayerListSheet;
